import { ALLOWED_TAGS } from './edit-text-serialize';
import { getColorFromSpan } from './text-color-ops';

export type DomPoint = { node: Node; offset: number };

type WalkContext = {
  acc: number;
  targetOffset: number;
  done: boolean;
  result: DomPoint | null;
};

function captureAtOffset(ctx: WalkContext, point: DomPoint): void {
  if (ctx.done || ctx.result !== null) return;
  if (ctx.acc === ctx.targetOffset) {
    ctx.result = point;
    ctx.done = true;
  }
}

function advanceVirtual(ctx: WalkContext, length: number, point: DomPoint): void {
  if (ctx.done) return;
  if (ctx.targetOffset >= ctx.acc && ctx.targetOffset < ctx.acc + length) {
    ctx.result = point;
    ctx.done = true;
    return;
  }
  ctx.acc += length;
}

function emitText(ctx: WalkContext, node: Text, charStart: number, charEnd: number): void {
  if (ctx.done) return;
  const len = charEnd - charStart;
  if (ctx.targetOffset >= ctx.acc && ctx.targetOffset <= ctx.acc + len) {
    ctx.result = { node, offset: charStart + (ctx.targetOffset - ctx.acc) };
    ctx.done = true;
    return;
  }
  ctx.acc += len;
}

function walkChildren(
  ctx: WalkContext,
  parent: Node,
  count: number,
): void {
  const children = parent.childNodes;
  for (let i = 0; i < count; i++) {
    if (ctx.done) return;
    const child = children[i];
    if (!child) continue;
    if (child.nodeType === Node.TEXT_NODE) {
      emitText(ctx, child as Text, 0, (child.textContent ?? '').length);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      walkElementForPoint(ctx, child as HTMLElement);
    }
  }
}

function walkElementForPoint(ctx: WalkContext, el: HTMLElement): void {
  if (!el?.tagName) return;
  if (el.tagName === 'SPAN' && el.classList.contains('ref-chip')) {
    const tokenLen = `{{ref:${el.getAttribute('data-ref-id') ?? ''}}}`.length;
    const parent = el.parentNode;
    if (parent) {
      const index = [...parent.childNodes].indexOf(el);
      advanceVirtual(ctx, tokenLen, { node: parent, offset: index });
    } else {
      ctx.acc += tokenLen;
    }
    return;
  }

  if (el.tagName === 'SPAN') {
    const colorHex = getColorFromSpan(el);
    if (colorHex) {
      const openLen = `<span style="color:${colorHex}">`.length;
      advanceVirtual(ctx, openLen, { node: el, offset: 0 });
      if (ctx.done) return;
      walkChildren(ctx, el, el.childNodes.length);
      if (ctx.done) return;
      advanceVirtual(ctx, '</span>'.length, {
        node: el,
        offset: el.childNodes.length,
      });
      return;
    }
  }

  const tag = el.tagName.toLowerCase();
  const allowed = ALLOWED_TAGS.has(el.tagName);

  if (allowed && tag === 'br') {
    advanceVirtual(ctx, '<br>'.length, { node: el.parentNode ?? el, offset: 0 });
    return;
  }

  const normalised = tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag;

  if (allowed) {
    advanceVirtual(ctx, `<${normalised}>`.length, { node: el, offset: 0 });
    if (ctx.done) return;
  }
  walkChildren(ctx, el, el.childNodes.length);
  if (ctx.done) return;
  if (allowed) {
    advanceVirtual(ctx, `</${normalised}>`.length, {
      node: el,
      offset: el.childNodes.length,
    });
  }
}

/**
 * Map a serialized-text offset to a DOM point inside `root`.
 * Returns null when the offset is out of range.
 */
export function findDomPointAtSerializedOffset(
  root: HTMLElement,
  targetOffset: number,
): DomPoint | null {
  const ctx: WalkContext = {
    acc: 0,
    targetOffset,
    done: false,
    result: null,
  };

  captureAtOffset(ctx, { node: root, offset: 0 });
  if (ctx.done) return ctx.result;

  walkChildren(ctx, root, root.childNodes.length);

  if (!ctx.done && ctx.acc === targetOffset) {
    return { node: root, offset: root.childNodes.length };
  }

  return ctx.result;
}

type OffsetWalkContext = {
  acc: number;
  targetNode: Node;
  targetOffset: number;
  done: boolean;
};

function emitTextForOffset(ctx: OffsetWalkContext, node: Text): void {
  if (node === ctx.targetNode) {
    ctx.acc += ctx.targetOffset;
    ctx.done = true;
    return;
  }
  ctx.acc += (node.textContent ?? '').length;
}

function walkElementForOffset(ctx: OffsetWalkContext, el: HTMLElement): void {
  if (el === ctx.targetNode) {
    walkChildrenForOffset(ctx, el, ctx.targetOffset);
    ctx.done = true;
    return;
  }

  if (el.tagName === 'SPAN' && el.classList.contains('ref-chip')) {
    ctx.acc += `{{ref:${el.getAttribute('data-ref-id') ?? ''}}}`.length;
    return;
  }

  if (el.tagName === 'SPAN') {
    const colorHex = getColorFromSpan(el);
    if (colorHex) {
      ctx.acc += `<span style="color:${colorHex}">`.length;
      walkChildrenForOffset(ctx, el, el.childNodes.length);
      if (ctx.done) return;
      ctx.acc += '</span>'.length;
      return;
    }
  }

  const tag = el.tagName.toLowerCase();
  const allowed = ALLOWED_TAGS.has(el.tagName);

  if (allowed && tag === 'br') {
    ctx.acc += '<br>'.length;
    return;
  }

  const normalised = tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag;

  if (allowed) {
    ctx.acc += `<${normalised}>`.length;
  }
  walkChildrenForOffset(ctx, el, el.childNodes.length);
  if (ctx.done) return;
  if (allowed) {
    ctx.acc += `</${normalised}>`.length;
  }
}

function walkChildrenForOffset(
  ctx: OffsetWalkContext,
  parent: Node,
  count: number,
): void {
  const children = parent.childNodes;
  for (let i = 0; i < count; i++) {
    if (ctx.done) return;
    const child = children[i];
    if (!child) continue;
    if (child.nodeType === Node.TEXT_NODE) {
      emitTextForOffset(ctx, child as Text);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      walkElementForOffset(ctx, child as HTMLElement);
    }
  }
}

/**
 * Walk `root` accumulating serialized-text char count until reaching
 * `targetNode` at `targetOffset`. Returns the count at that point or
 * `null` if the target isn't found within `root`.
 */
export function computeSerializedOffsetAt(
  root: HTMLElement,
  targetNode: Node,
  targetOffset: number,
): number | null {
  const ctx: OffsetWalkContext = {
    acc: 0,
    targetNode,
    targetOffset,
    done: false,
  };

  if (root === targetNode) {
    walkChildrenForOffset(ctx, root, targetOffset);
    return ctx.acc;
  }

  walkChildrenForOffset(ctx, root, root.childNodes.length);
  return ctx.done ? ctx.acc : null;
}
